import { Controller } from '@hotwired/stimulus';

// data-controller="styleguide-search"
export default class extends Controller {
  static targets = ['content', 'controls', 'counter'];
  static accordionSelector = "[data-controller='accordion']";

  // === Helper getters ===
  get root() {
    return this.hasContentTarget ? this.contentTarget : document;
  }

  get accordions() {
    return this.root.querySelectorAll(this.constructor.accordionSelector);
  }

  connect() {
    this.matches = [];
    this.currentIndex = -1;
    this.updateUI();
  }

  // === Filtering logic ===
  filter(event) {
    const q = event.target.value.trim().toLowerCase();
    this.clearHighlights();
    this.matches = [];
    this.currentIndex = -1;
    this.updateCounter();

    if (!q) {
      this.accordions.forEach((accordion) => this.closeAccordion(accordion));
      this.updateUI();
      return;
    }

    const matchingAccordions = new Set();

    this.accordions.forEach((accordion) => {
      const text = accordion.textContent.toLowerCase();
      if (!text.includes(q)) return;

      matchingAccordions.add(accordion);
      this.openAccordion(accordion);

      const heading = accordion.querySelector("[data-accordion-target='button']");
      const panel = accordion.querySelector("[data-accordion-target='panel']");
      if (heading) this.highlightTextNodes(heading, q);
      if (panel) this.highlightTextNodes(panel, q);
    });

    this.accordions.forEach((accordion) => {
      if (!matchingAccordions.has(accordion)) this.closeAccordion(accordion);
    });

    // Collect all matches *within root only*
    this.matches = Array.from(this.root.querySelectorAll('mark.bg-yellow-200'));
    if (this.matches.length > 0) {
      this.currentIndex = 0;
      this.scrollToCurrent();
    }
    this.updateCounter();
    this.updateUI();
  }

  openAccordion(accordion) {
    const controller = this.getAccordionController(accordion);
    if (controller) {
      controller.openValue = true;
      if (typeof controller.open === 'function') controller.open();
    }
  }

  closeAccordion(accordion) {
    const controller = this.getAccordionController(accordion);
    if (controller) {
      controller.openValue = false;
      if (typeof controller.close === 'function') controller.close();
    }
  }

  // === Highlighting logic ===
  highlightTextNodes(container, query) {
    if (!query) return;
    const regex = new RegExp(this.escapeRegex(query), 'gi');

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (node.parentNode.closest('mark, pre, code')) return NodeFilter.FILTER_REJECT;
        if (!node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((node) => {
      const text = node.nodeValue;
      const matches = [...text.matchAll(regex)];
      if (matches.length === 0) return;

      const frag = document.createDocumentFragment();
      let lastIndex = 0;

      matches.forEach((m) => {
        const start = m.index;
        const end = start + m[0].length;
        if (start > lastIndex)
          frag.appendChild(document.createTextNode(text.slice(lastIndex, start)));

        const mark = document.createElement('mark');
        mark.className = 'bg-yellow-200';
        mark.textContent = text.slice(start, end);
        frag.appendChild(mark);
        lastIndex = end;
      });
      if (lastIndex < text.length) frag.appendChild(document.createTextNode(text.slice(lastIndex)));

      node.parentNode.replaceChild(frag, node);
    });
  }

  clearHighlights() {
    this.accordions.forEach((accordion) => {
      accordion.querySelectorAll('mark').forEach((mark) => {
        mark.replaceWith(document.createTextNode(mark.textContent));
      });
      this.mergeTextNodes(accordion);
    });
  }

  mergeTextNodes(element) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);
    let node = walker.nextNode();
    while (node) {
      let next = node.nextSibling;
      while (next && next.nodeType === Node.TEXT_NODE) {
        node.nodeValue += next.nodeValue;
        next.parentNode.removeChild(next);
        next = node.nextSibling;
      }
      node = walker.nextNode();
    }
  }

  // === Navigation ===
  next() {
    if (this.matches.length === 0) return;
    this.currentIndex = (this.currentIndex + 1) % this.matches.length;
    this.scrollToCurrent();
    this.updateCounter();
  }

  prev() {
    if (this.matches.length === 0) return;
    this.currentIndex = (this.currentIndex - 1 + this.matches.length) % this.matches.length;
    this.scrollToCurrent();
    this.updateCounter();
  }

  scrollToCurrent() {
    this.matches.forEach((m) => m.classList.remove('ring', 'ring-yellow-400'));
    const current = this.matches[this.currentIndex];
    if (!current) return;
    current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    current.classList.add('ring', 'ring-yellow-400');
  }

  // === UI updates ===
  updateCounter() {
    if (!this.hasCounterTarget) return;
    if (this.matches.length === 0) {
      this.counterTarget.textContent = '0 of 0';
    } else {
      this.counterTarget.textContent = `${this.currentIndex + 1} of ${this.matches.length}`;
    }
  }

  updateUI() {
    const total = this.matches.length;
    const visible = total > 0;
    this.controlsTarget.classList.toggle('hidden', !visible);
    this.counterTarget.textContent = visible ? `${this.currentIndex + 1} of ${total}` : '0 of 0';
  }

  getAccordionController(element) {
    try {
      return this.application.getControllerForElementAndIdentifier(element, 'accordion');
    } catch {
      return null;
    }
  }

  // === Utility ===
  escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
